import { useEffect, useRef } from "react";

/**
 * FlowAmbience 2.0 — the living, premium backdrop behind the whole Flow app.
 *
 * One lightweight 2D canvas, fixed behind everything (z-0, pointer-events-none):
 *   • three parallax star layers (far/mid/near) that drift + twinkle,
 *     now with a soft MOUSE PARALLAX — the sky leans toward the cursor per depth
 *   • two flowing AURORA RIBBONS — long sine bands that undulate across the
 *     upper sky like northern lights (additive, very low alpha, slow phase)
 *   • a slow, wide "milky" dust band that breathes
 *   • rare, GOOD shooting stars — bright head, tapered gradient trail,
 *     fast eased arc, clean fade (never more than one at a time)
 *   • occasional CONSTELLATION FLASH — 4–6 nearby stars link up with faint
 *     lines for a few seconds, then dissolve (a tiny "the sky is alive" moment)
 *
 * Perf: capped DPR, pauses when the tab is hidden, star count throttled to
 * viewport area, ribbons drawn with ~40 segments each, and everything fully
 * disables under prefers-reduced-motion (static field fallback).
 */
export default function FlowAmbience() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const DPR = Math.min(window.devicePixelRatio || 1, 1.75);
    let W = 0, H = 0, raf = 0, t = 0, running = true;

    /* ── mouse parallax (eased, tiny) ─────────────────────────────────────── */
    let mx = 0, my = 0, tmx = 0, tmy = 0; // -0.5 … 0.5
    const onPointer = (e) => {
      tmx = e.clientX / window.innerWidth - 0.5;
      tmy = e.clientY / window.innerHeight - 0.5;
    };
    window.addEventListener("pointermove", onPointer, { passive: true });

    /* ── star layers ──────────────────────────────────────────────────────── */
    const PALETTE = ["#ffffff", "#c4b5fd", "#a5f3fc", "#fbcfe8", "#ddd6fe"];
    let layers = [];

    const buildLayers = () => {
      const area = W * H;
      const mk = (count, depth, sizeMin, sizeMax, base) =>
        Array.from({ length: count }, () => ({
          x: Math.random() * W,
          y: Math.random() * H,
          r: sizeMin + Math.random() * (sizeMax - sizeMin),
          base,
          tw: Math.random() * Math.PI * 2,
          tws: 0.4 + Math.random() * 1.4,
          depth,
          c: PALETTE[(Math.random() * PALETTE.length) | 0],
        }));
      layers = [
        mk(Math.round(area / 5200), 0.010, 0.4, 1.0, 0.35), // far
        mk(Math.round(area / 11000), 0.024, 0.7, 1.6, 0.6), // mid
        mk(Math.round(area / 26000), 0.050, 1.1, 2.4, 0.95), // near
      ];
    };

    /* ── aurora ribbons ──────────────────────────────────────────────────── */
    // Each ribbon: a band of stacked sine strokes, additive, drifting phase.
    const RIBBONS = [
      { yBase: 0.16, amp: 0.045, freq: 1.6, speed: 0.10, hue: "139,92,246", alpha: 0.045, width: 46 },
      { yBase: 0.26, amp: 0.060, freq: 1.1, speed: -0.07, hue: "6,182,212",  alpha: 0.035, width: 60 },
    ];
    const drawRibbon = (rb) => {
      const SEG = 40;
      const yb = H * rb.yBase + Math.sin(t * 0.07 + rb.yBase * 20) * H * 0.02;
      ctx.beginPath();
      for (let i = 0; i <= SEG; i++) {
        const x = (i / SEG) * W;
        const y = yb
          + Math.sin((i / SEG) * Math.PI * 2 * rb.freq + t * rb.speed * 6) * H * rb.amp
          + Math.sin((i / SEG) * Math.PI * 5 + t * rb.speed * 3) * H * rb.amp * 0.3;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      const breathe = 0.75 + 0.25 * Math.sin(t * 0.23 + rb.yBase * 40);
      ctx.strokeStyle = `rgba(${rb.hue},${rb.alpha * breathe})`;
      ctx.lineWidth = rb.width;
      ctx.lineCap = "round";
      ctx.filter = "blur(14px)";
      ctx.stroke();
      // bright core line
      ctx.strokeStyle = `rgba(${rb.hue},${rb.alpha * 1.6 * breathe})`;
      ctx.lineWidth = rb.width * 0.3;
      ctx.stroke();
      ctx.filter = "none";
    };

    /* ── shooting stars ──────────────────────────────────────────────────── */
    let shoot = null;
    let nextShootAt = 2 + Math.random() * 5;

    const spawnShoot = () => {
      const fromLeft = Math.random() > 0.5;
      const sx = fromLeft ? -60 : W * (0.4 + Math.random() * 0.5);
      const sy = Math.random() * H * 0.45;
      const ang = (fromLeft ? 0.28 : 0.62) + (Math.random() - 0.5) * 0.18;
      const speed = 900 + Math.random() * 650;
      shoot = {
        x: sx, y: sy,
        vx: Math.cos(ang) * speed,
        vy: Math.sin(ang) * speed,
        life: 0,
        ttl: 0.55 + Math.random() * 0.4,
        len: 130 + Math.random() * 120,
        hue: Math.random() > 0.5 ? "#c4b5fd" : "#a5f3fc",
      };
    };

    /* ── constellation flash ─────────────────────────────────────────────── */
    let constel = null;                 // { stars: [...], life, ttl }
    let nextConstelAt = 9 + Math.random() * 10;
    const spawnConstel = () => {
      const near = layers[2] || [];
      if (near.length < 6) return;
      // pick a seed star not too close to an edge, then its 4 nearest siblings
      const seed = near[(Math.random() * near.length) | 0];
      const rest = near
        .filter((s) => s !== seed)
        .map((s) => ({ s, d: (s.x - seed.x) ** 2 + (s.y - seed.y) ** 2 }))
        .sort((a, b) => a.d - b.d)
        .slice(0, 3 + ((Math.random() * 3) | 0))
        .filter((e) => e.d < (Math.min(W, H) * 0.38) ** 2)
        .map((e) => e.s);
      if (rest.length < 3) return;
      constel = { stars: [seed, ...rest], life: 0, ttl: 5 + Math.random() * 3 };
    };

    /* ── sizing ──────────────────────────────────────────────────────────── */
    const resize = () => {
      W = canvas.clientWidth;
      H = canvas.clientHeight;
      canvas.width = Math.round(W * DPR);
      canvas.height = Math.round(H * DPR);
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      buildLayers();
      constel = null;
    };
    resize();
    window.addEventListener("resize", resize);

    /* ── static fallback (reduced motion) ────────────────────────────────── */
    if (reduced) {
      ctx.clearRect(0, 0, W, H);
      for (const layer of layers) {
        for (const s of layer) {
          ctx.globalAlpha = s.base * 0.8;
          ctx.fillStyle = s.c;
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.r, 0, 7);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
      return () => {
        window.removeEventListener("resize", resize);
        window.removeEventListener("pointermove", onPointer);
      };
    }

    /* ── frame ───────────────────────────────────────────────────────────── */
    let last = performance.now();
    const frame = (now) => {
      if (!running) return;
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      t += dt;
      ctx.clearRect(0, 0, W, H);

      // eased mouse
      mx += (tmx - mx) * 0.04;
      my += (tmy - my) * 0.04;

      // wide drifting dust band — the "milky way" breathing
      const bandY = H * (0.42 + Math.sin(t * 0.05) * 0.04);
      const grad = ctx.createLinearGradient(0, bandY - H * 0.32, W, bandY + H * 0.32);
      grad.addColorStop(0, "rgba(124,58,237,0)");
      grad.addColorStop(0.5, `rgba(139,92,246,${0.05 + Math.sin(t * 0.12) * 0.015})`);
      grad.addColorStop(0.72, `rgba(6,182,212,${0.035 + Math.cos(t * 0.1) * 0.012})`);
      grad.addColorStop(1, "rgba(6,182,212,0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      // aurora ribbons over the dust, under the stars
      ctx.globalCompositeOperation = "lighter";
      for (const rb of RIBBONS) drawRibbon(rb);
      ctx.globalCompositeOperation = "source-over";

      // parallax star drift + mouse lean (deeper layers move more with cursor)
      for (const layer of layers) {
        for (const s of layer) {
          s.x -= s.depth * 14 * dt * 60 * 0.016;
          if (s.x < -4) { s.x = W + 4; s.y = Math.random() * H; }
          s.tw += s.tws * dt;
          const tw = 0.55 + 0.45 * Math.sin(s.tw);
          const px = s.x + mx * s.depth * -420;   // lean opposite the cursor → depth
          const py = s.y + my * s.depth * -260;
          ctx.globalAlpha = s.base * tw;
          ctx.fillStyle = s.c;
          ctx.beginPath();
          ctx.arc(px, py, s.r, 0, 7);
          ctx.fill();
          if (s.depth > 0.04 && tw > 0.85) {
            ctx.globalAlpha = s.base * (tw - 0.85) * 2;
            ctx.fillRect(px - s.r * 3, py - 0.4, s.r * 6, 0.8);
            ctx.fillRect(px - 0.4, py - s.r * 3, 0.8, s.r * 6);
          }
        }
      }
      ctx.globalAlpha = 1;

      // constellation flash — link nearby near-layer stars, fade in/out
      nextConstelAt -= dt;
      if (!constel && nextConstelAt <= 0) {
        spawnConstel();
        nextConstelAt = 14 + Math.random() * 14;
      }
      if (constel) {
        constel.life += dt;
        const p = constel.life / constel.ttl;
        const a = p < 0.18 ? p / 0.18 : p > 0.7 ? Math.max(0, 1 - (p - 0.7) / 0.3) : 1;
        if (p >= 1) {
          constel = null;
        } else {
          const pts = constel.stars.map((s) => ({
            x: s.x + mx * s.depth * -420,
            y: s.y + my * s.depth * -260,
          }));
          ctx.strokeStyle = `rgba(196,181,253,${0.16 * a})`;
          ctx.lineWidth = 0.8;
          ctx.beginPath();
          for (let i = 0; i < pts.length; i++) {
            if (i === 0) ctx.moveTo(pts[i].x, pts[i].y);
            else ctx.lineTo(pts[i].x, pts[i].y);
          }
          ctx.stroke();
          // glow up the member stars
          for (const q of pts) {
            ctx.globalAlpha = 0.5 * a;
            ctx.fillStyle = "#ddd6fe";
            ctx.beginPath();
            ctx.arc(q.x, q.y, 1.9, 0, 7);
            ctx.fill();
          }
          ctx.globalAlpha = 1;
        }
      }

      // shooting star scheduling
      nextShootAt -= dt;
      if (!shoot && nextShootAt <= 0) {
        spawnShoot();
        nextShootAt = 5 + Math.random() * 9;
      }
      if (shoot) {
        shoot.life += dt;
        shoot.x += shoot.vx * dt;
        shoot.y += shoot.vy * dt;
        const p = shoot.life / shoot.ttl;
        const fade = p < 0.2 ? p / 0.2 : 1 - (p - 0.2) / 0.8;
        const a = Math.max(0, fade);
        const tx = shoot.x - (shoot.vx / Math.hypot(shoot.vx, shoot.vy)) * shoot.len;
        const ty = shoot.y - (shoot.vy / Math.hypot(shoot.vx, shoot.vy)) * shoot.len;
        const tg = ctx.createLinearGradient(shoot.x, shoot.y, tx, ty);
        tg.addColorStop(0, `rgba(255,255,255,${a})`);
        tg.addColorStop(0.25, `${shoot.hue}${Math.round(a * 200).toString(16).padStart(2, "0")}`);
        tg.addColorStop(1, "rgba(255,255,255,0)");
        ctx.strokeStyle = tg;
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(shoot.x, shoot.y);
        ctx.lineTo(tx, ty);
        ctx.stroke();
        ctx.globalAlpha = a;
        ctx.fillStyle = "#ffffff";
        ctx.shadowColor = shoot.hue;
        ctx.shadowBlur = 12;
        ctx.beginPath();
        ctx.arc(shoot.x, shoot.y, 1.8, 0, 7);
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 1;
        if (p >= 1 || shoot.x > W + 120 || shoot.y > H + 120) shoot = null;
      }

      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    /* ── pause when hidden ───────────────────────────────────────────────── */
    const onVis = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        last = performance.now();
        raf = requestAnimationFrame(frame);
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", onPointer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 0, width: "100%", height: "100%" }}
      aria-hidden
    />
  );
}
